import {
  AbstractControl,
  FormArray,
  FormControl,
  FormGroup,
  NgControl,
  UntypedFormArray,
  UntypedFormControl,
  UntypedFormGroup,
  ValidationErrors,
} from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';

import { SpaceBeforeCapitalPipe } from '../pipes/space-before-capital.pipe';

export class FormGroupExtension {
  static markFormAsDirtyAndTouched(form: UntypedFormGroup | FormArray) {
    form.updateValueAndValidity();
    form.markAsDirty({ markAsDirtyAll: true } as unknown);
    form.markAsTouched();
    Object.keys(form.controls).forEach((key) => {
      (form.controls as unknown)[key].markAsDirty({ markAsDirtyAll: true } as unknown);
      (form.controls as unknown)[key].markAsTouched();
      if (((form.controls as unknown)[key] as unknown)['controls']) {
        this.markFormAsDirtyAndTouched((form.controls as unknown)[key] as UntypedFormGroup);
      }
    });
  }

  static enableForm(form: UntypedFormGroup | FormArray) {
    form.enable({ emitEvent: false });

    Object.values(form.controls).forEach((control: UntypedFormGroup | FormArray) => {
      control.enable({ emitEvent: false });

      if (control.controls) {
        this.enableForm(control);
      }
    });

    form.updateValueAndValidity({ emitEvent: false });
  }

  static getFormValidationErrorsMessage(
    form: UntypedFormGroup | FormArray,
    translate: TranslateService,
    parentKey: string = undefined
  ): string {
    return this.getFormValidationErrors(form, parentKey, undefined, translate)
      .map((x) => x.message)
      .join(',\n');
  }

  static hasAnyValidationError(form: UntypedFormGroup | FormArray, parentKey: string = undefined): boolean {
    return form?.invalid || this.getFormValidationErrors(form, parentKey).some((x) => x);
  }

  static getFormValidationErrors(
    form: UntypedFormGroup | FormArray,
    parentKey: string = undefined,
    message: string | undefined = undefined,
    translate: TranslateService = undefined
  ): AllValidationErrors[] {
    let errors: AllValidationErrors[] = [];
    if (!form?.controls) {
      return errors;
    }

    Object.keys(form?.controls).forEach((key) => {
      const control = form.controls[key];

      const rowText = translate?.instant('error.row') ?? 'row';
      const inText = translate?.instant('error.in') ?? 'in';
      const atText = translate?.instant('error.at') ?? 'at';
      const invalidText = translate?.instant('error.invalid') ?? 'Invalid';

      if (control instanceof FormGroup) {
        errors = errors.concat(
          FormGroupExtension.getFormValidationErrors(
            control,
            parentKey ? `${parentKey}.${key}` : key,
            (parentKey
              ? `${SpaceBeforeCapitalPipe.transform(key)} ${inText} ${SpaceBeforeCapitalPipe.transform(parentKey)} `
              : control.parent instanceof FormArray
                ? `${+key + 1} ${rowText} ${inText} ${SpaceBeforeCapitalPipe.transform(this.getControlName(control.parent))} `
                : `${SpaceBeforeCapitalPipe.transform(key)} `) + (message ?? ''),
            translate
          )
        );
      }
      if (control instanceof FormArray) {
        control?.controls.forEach((item, index) => {
          errors = errors.concat(
            FormGroupExtension.getFormValidationErrors(
              item as FormGroup,
              parentKey ? `${parentKey}.${key}[${index}]` : `${key}[${index}]`,
              `${SpaceBeforeCapitalPipe.transform(key)} ${atText} ${index + 1} ${rowText} ` +
                (message ? `${inText} ${message}` : ''),
              translate
            )
          );
        });
      }
      const controlErrors: ValidationErrors = form.controls[key]?.errors;
      if (controlErrors) {
        Object.keys(controlErrors).forEach((keyError) => {
          const translateKey = `error.${keyError}`;
          let errorText = translate?.instant(translateKey);
          if (!errorText || errorText === translateKey) {
            errorText = keyError;
          }

          errors.push({
            control_name: parentKey ? `${parentKey}.${key}` : key,
            error_name: keyError,
            error_value: controlErrors[keyError],
            formValue: form.value,
            value: form.controls[key].value,
            message:
              `${invalidText} '${SpaceBeforeCapitalPipe.transform(key)}' ` +
              (message ? `${inText} ${message}` : '') +
              `- ${errorText}`,
          });
        });
      }
    });
    return errors;
  }

  static markFormAssDirty(form: UntypedFormGroup | FormArray) {
    form.updateValueAndValidity();
    Object.keys(form.controls).forEach((key) => {
      (form.controls as unknown)[key].markAsDirty({ markAsDirtyAll: true } as unknown);
      if (((form.controls as unknown)[key] as unknown)['controls']) {
        this.markFormAssDirty((form.controls as unknown)[key] as UntypedFormGroup);
      }
    });
  }

  static markFormAssTouched(form: UntypedFormGroup | FormArray) {
    form.updateValueAndValidity();
    Object.keys(form.controls).forEach((key) => {
      (form.controls as unknown)[key].markAsTouched({ markAsDirtyAll: true } as unknown);
      if (((form.controls as unknown)[key] as unknown)['controls']) {
        this.markFormAssTouched((form.controls as unknown)[key] as UntypedFormGroup);
      }
    });
  }

  static updateValueAndValidity<T extends AbstractControl>(control: T): void {
    if (control instanceof FormGroup || control instanceof UntypedFormGroup) {
      control.updateValueAndValidity();

      const controls = control.controls;
      Object.keys(controls).forEach((key) => {
        this.updateValueAndValidity(controls[key]);
      });
    } else if (control instanceof FormArray || control instanceof UntypedFormArray) {
      control.updateValueAndValidity();

      control.controls.forEach((formControl) => {
        this.updateValueAndValidity(formControl);
      });
    } else if (control instanceof FormControl || control instanceof UntypedFormControl) {
      control.updateValueAndValidity();
    } else {
      throw new Error('Error: unexpected control value');
    }
  }

  static getControlName(control: AbstractControl) {
    const parent = control.parent;
    if (parent instanceof FormGroup || parent instanceof UntypedFormControl || parent instanceof FormArray) {
      return Object.keys(parent.controls).find((name) => control === parent.controls[name]);
    }
    return null;
  }

  static getControlPath(ctrl: NgControl): string {
    const current: AbstractControl | null = ctrl.control;
    return FormGroupExtension.getAbstractControlPath(current);
  }

  static getAbstractControlPath(ctrl: AbstractControl): string {
    const path: string[] = [];
    while (ctrl && ctrl.parent) {
      const parent = ctrl.parent;
      const name = Object.keys(parent.controls).find((key) => parent.get(key) === ctrl);
      if (name) path.unshift(name);
      ctrl = parent;
    }

    return path.join('.');
  }

  static getMarkerPath(ctrl: AbstractControl, idtrackControlNames: string | string[] = 'id'): string | undefined {
    const fields = Array.isArray(idtrackControlNames) ? idtrackControlNames : [idtrackControlNames];
    const path: string[] = [];
    while (ctrl && ctrl.parent) {
      const parent = ctrl.parent;

      if (parent instanceof FormArray) {
        const idx = parent.controls.indexOf(ctrl);
        const ids = fields.filter((f) => ctrl.value && ctrl.value[f] !== undefined).map((f) => `${f}:${ctrl.value[f]}`);

        if (ids.length > 0) {
          path.unshift(`[${ids.join(';')}]`);
        } else {
          path.unshift(`${idx}`);
        }
      } else {
        const name = Object.keys(parent.controls).find((key) => parent.get(key) === ctrl);
        if (name) path.unshift(name);
      }

      ctrl = parent;
    }

    return path.length ? path.join('.') : undefined;
  }
}

export interface AllValidationErrors {
  control_name: string;
  error_name: string;
  error_value: unknown;
  formValue: unknown;
  value: unknown;
  message: string;
}

export interface FormGroupControls {
  [key: string]: AbstractControl;
}
